// /circle-of-fifths — an interactive circle-of-fifths explorer and practice
// room. Explore mode: click around the wheel to see each key's signature,
// relative minor, neighbors, and diatonic chords (tap to hear). Inversions
// mode: prompts like "F major — 1st inversion", judged from what you play.
// Sevenths mode: the same Learn/Drill split for four-note chords — diatonic
// seventh qualities, the V7-points-home arrow, and a ii–V–I lap around the
// wheel. Find-the-key mode: key-signature / relative-minor questions answered
// by clicking the wheel. Input reuses the /piano-drills stack — MIDI keyboard,
// on-screen keys, or the A–K row — plus its toggleable VexFlow sheet view.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  DIATONIC_CHORDS,
  KB_KEYS,
  chordLabel,
  kbAnchor,
  keyByName,
} from "../lib/pianoDrills";
import { ensureAudio, playTone } from "../lib/pianoDrillsAudio";
import {
  FLAT_ORDER,
  INVERSIONS,
  MINOR_DIATONIC,
  SEVENTH_INVERSIONS,
  anchoredMinorTriad,
  anchoredTriad,
  brightnessStep,
  modeLadder,
  checkInversion,
  detectInversion,
  detectSeventhInversion,
  iiVIChords,
  inversionChordLabel,
  loadStats,
  minorChordLabel,
  minorSeventhLabel,
  randomDominantPrompt,
  randomInversionPrompt,
  randomSeventhPrompt,
  randomWheelPrompt,
  recordResult,
  saveStats,
  scaleNotes,
  seventhBassName,
  seventhLabel,
  seventhPcs,
  seventhQuality,
  seventhSlashLabel,
  signatureLabel,
  smoothSeventh,
  targetInversionMidis,
  targetMinorInversionMidis,
  targetSeventhInversionMidis,
  targetMinorSeventhInversionMidis,
  wheelDegreeBadges,
  wheelEntry,
  wheelNeighbors,
} from "../lib/circleOfFifths";
import { useMidiInput } from "../components/synthlab/useSynthInput";
import PianoRoll from "../components/pianoDrills/PianoRoll";
import SheetView from "../components/pianoDrills/SheetView";
import Wheel from "../components/circleOfFifths/Wheel";
import MiniChordDiagram from "../components/circleOfFifths/MiniChordDiagram";
import { cn } from "../lib/utils";

const LOW = 48; // C3
const HIGH = 84; // C6
const EMPTY_SET = new Set();
const NOOP = () => {};

const MODES = [
  { id: "explore", label: "Explore" },
  { id: "inversions", label: "Inversions" },
  { id: "sevenths", label: "Sevenths", wip: true },
  { id: "wheel", label: "Find the key" },
];

const INVERSION_SUB_TABS = [
  { id: "learn", label: "Learn" },
  { id: "drill", label: "Drill" },
];

// Sevenths mode's three drill flavors.
const SEVENTH_DRILLS = [
  { id: "play", label: "Play it" },
  { id: "findKey", label: "Find the key" },
  { id: "trip", label: "ii–V–I trip" },
];

// Chip tint per diatonic-seventh quality — the lone dominant (amber) should
// pop out of the row; that's the lesson.
const QUALITY_CHIP = {
  maj7: "border-indigo-300 bg-indigo-50 text-indigo-700",
  m7: "border-sky-300 bg-sky-50 text-sky-700",
  dom7: "border-amber-400 bg-amber-100 text-amber-800",
  halfDim: "border-rose-300 bg-rose-50 text-rose-700",
};

// Roman-numeral suffix per quality: lower-case romans already carry the
// minorness, so ii7 (not iim7); the diminished ° is replaced by ø7.
const ROMAN_7_SUFFIX = { maj7: "maj7", m7: "7", dom7: "7", halfDim: "ø7" };
const seventhRoman = (roman, quality) =>
  `${roman.replace("°", "")}${ROMAN_7_SUFFIX[quality] || "7"}`;

const WRONG_HINTS = {
  wrongNotes: "Not those notes — listen again and retry.",
  wrongBass: "Right chord — wrong note on the bottom.",
};

// A one-chord, whole-note "drill" so SheetView can render a single voicing.
const chordSheetDrill = (keyName, label, midis) => {
  const key = keyByName(keyName);
  if (!key || !midis) return null;
  return {
    keyName,
    useFlats: key.useFlats,
    rhythmId: "straight",
    bassId: "off",
    low: LOW,
    high: HIGH,
    bars: [{ degree: 1, roman: "", label, midis, bassMidi: null }],
    events: [{ step: 0, midis, durSteps: 16, hand: "rh", bar: 0, absStep: 0 }],
    totalSteps: 16,
    error: null,
  };
};

const StatChip = ({ label, stat }) => (
  <span className="rounded-full border border-slate-200 bg-white/80 px-2.5 py-1 text-xs text-slate-600">
    {label}: {stat.correct}/{stat.attempts} · streak {stat.streak}
    {stat.best > 0 && ` (best ${stat.best})`}
  </span>
);

export default function CircleOfFifths() {
  const [keyName, setKeyName] = useState("C");
  // Set by clicking the wheel's inner (minor) vs outer (major) ring — drives
  // explore mode's panel and, in inversions mode, which key's chords drill.
  const [perspective, setPerspective] = useState("major"); // "major" | "minor"
  const [mode, setMode] = useState("explore");
  const [showSheet, setShowSheet] = useState(true);
  const [activeMidis, setActiveMidis] = useState(EMPTY_SET);
  const [exploreChord, setExploreChord] = useState({ degree: 1 });
  const [prompt, setPrompt] = useState(() => randomInversionPrompt("C"));
  const [promptStatus, setPromptStatus] = useState(null); // null | "correct" | "wrongNotes" | "wrongBass"
  const [reveal, setReveal] = useState(false);
  const [wheelPrompt, setWheelPrompt] = useState(() => randomWheelPrompt());
  const [wheelFlash, setWheelFlash] = useState(null);
  // The last brighter/darker step taken (via the Explore panel's buttons),
  // so the scale display can highlight the one note that just changed.
  // { direction: "brighter" | "darker", from, to } | null.
  const [lastStep, setLastStep] = useState(null);
  const [stats, setStats] = useState(() => loadStats());

  // Inversions mode's Learn | Drill sub-tab — starts on Learn for a
  // brand-new learner (no attempts logged yet), Drill for a returning one.
  const [subTab, setSubTab] = useState(() =>
    loadStats().inversions.attempts === 0 ? "learn" : "drill"
  );
  // The lesson's chord (perspective-relative degree), its focused inversion
  // card (targets on the roll + sheet), and which inversion the learner last
  // played (null until they nail one).
  const [learnDegree, setLearnDegree] = useState(1);
  const [learnFocus, setLearnFocus] = useState(0);
  const [learnFound, setLearnFound] = useState(null);

  // Sevenths mode: its own Learn | Drill sub-tab (same fresh-learner default
  // as inversions, keyed off the sevenths stats bucket) plus a drill flavor.
  const [seventhsTab, setSeventhsTab] = useState(() =>
    loadStats().sevenths.attempts === 0 ? "learn" : "drill"
  );
  const [svDrill, setSvDrill] = useState("play");
  // Sevenths lesson: selected diatonic chord, focused inversion card, and the
  // last inversion the learner nailed (null until they do).
  const [svLearnDegree, setSvLearnDegree] = useState(1);
  const [svFocus, setSvFocus] = useState(0);
  const [svLearnFound, setSvLearnFound] = useState(null);
  // Play-it drill prompt + judging status, mirroring the inversion drill.
  const [svPrompt, setSvPrompt] = useState(() => randomSeventhPrompt("C"));
  const [svStatus, setSvStatus] = useState(null); // null | "correct" | "wrongNotes" | "wrongBass"
  const [svReveal, setSvReveal] = useState(false);
  // Find-the-key drill: which dominant seventh is on the table.
  const [domPrompt, setDomPrompt] = useState(() => randomDominantPrompt());
  // ii–V–I trip: current key, which of the three chords is up, keys completed
  // (12 = a lap), and the brief "landed all three" flash.
  const [tripKey, setTripKey] = useState("C");
  const [tripStep, setTripStep] = useState(0);
  const [tripCount, setTripCount] = useState(0);
  const [tripFlash, setTripFlash] = useState(false);

  // Tap-to-select notes (mouse-friendly alternative to holding keys down).
  const [pickedMidis, setPickedMidis] = useState(EMPTY_SET);

  const heldRef = useRef(new Set());
  const wrongRecordedRef = useRef(false);
  const timeoutsRef = useRef([]);
  const later = useCallback((fn, ms) => {
    timeoutsRef.current.push(setTimeout(fn, ms));
  }, []);
  useEffect(() => () => timeoutsRef.current.forEach(clearTimeout), []);

  const entry = wheelEntry(keyName);
  const neighbors = wheelNeighbors(keyName);
  // The brighter (dominant) / darker (subdominant) neighbor and which single
  // note changes to get there — feeds the Explore panel's "Brighter · darker"
  // card.
  const darkerStep = brightnessStep(keyName, "darker");
  const brighterStep = brightnessStep(keyName, "brighter");
  // Same home note, seven modes brightest to darkest — the "Same home note,
  // darker scales" card just underneath.
  const modeRows = useMemo(() => modeLadder(keyName), [keyName]);

  const bumpStats = useCallback((drillMode, correct) => {
    setStats((prev) => {
      const next = recordResult(prev, drillMode, correct);
      saveStats(next);
      return next;
    });
  }, []);

  /* ---------- inversion drill ---------- */

  const targetMidis = useMemo(
    () =>
      prompt
        ? prompt.perspective === "minor"
          ? targetMinorInversionMidis(prompt.keyName, prompt.minorDegree, prompt.inversion, LOW, HIGH)
          : targetInversionMidis(prompt.keyName, prompt.degree, prompt.inversion, LOW, HIGH)
        : null,
    [prompt]
  );

  const nextInversionPrompt = useCallback(
    (forKey, forPerspective) => {
      setPrompt(randomInversionPrompt(forKey, Math.random, forPerspective));
      setPromptStatus(null);
      setReveal(false);
      setPickedMidis(EMPTY_SET);
      wrongRecordedRef.current = false;
    },
    []
  );

  // A new key or a flip between major/minor mid-drill means the old prompt
  // no longer matches — reroll.
  useEffect(() => {
    if (mode === "inversions") nextInversionPrompt(keyName, perspective);
  }, [keyName, mode, perspective, nextInversionPrompt]);

  // Reset the tap-selection and Learn-mode "got it" feedback whenever the
  // ground shifts under the drill/lesson: new key, flipped perspective, a
  // different mode, sub-tab, drill flavor, or lesson chord.
  useEffect(() => {
    setPickedMidis(EMPTY_SET);
    setLearnFound(null);
    setSvLearnFound(null);
  }, [keyName, perspective, mode, subTab, learnDegree, seventhsTab, svDrill, svLearnDegree]);

  // A new key or perspective means the lessons' chord rows changed meaning —
  // fall back to the tonic.
  useEffect(() => {
    setLearnDegree(1);
    setSvLearnDegree(1);
    setSvFocus(0);
  }, [keyName, perspective]);

  // Judge a set of midis against the live drill prompt: stats, status, and
  // auto-advance on success. Shared by the held-notes path (judgeHeld, below)
  // and tap-to-select's drill branch (tapToggle).
  const judgeNotes = useCallback(
    (notes) => {
      if (mode !== "inversions" || !prompt || promptStatus === "correct") return;
      if (notes.length < 3) return;
      const res = checkInversion(notes, prompt.keyName, prompt.degree, prompt.inversion);
      if (res.correct) {
        setPromptStatus("correct");
        setPickedMidis(EMPTY_SET);
        bumpStats("inversions", true);
        later(() => nextInversionPrompt(prompt.keyName, prompt.perspective), 1200);
      } else {
        setPromptStatus(res.reason);
        if (!wrongRecordedRef.current) {
          wrongRecordedRef.current = true;
          bumpStats("inversions", false);
        }
      }
    },
    [mode, prompt, promptStatus, bumpStats, later, nextInversionPrompt]
  );

  const judgeHeld = useCallback((held) => judgeNotes([...held]), [judgeNotes]);

  /* ---------- inversion learn mode ---------- */

  // The lesson covers the whole key, like the drill: a chord row picks which
  // diatonic chord (perspective-relative degree, 1–7) the inversion cards
  // below break down. inversionChordLabel and friends key off the chord's
  // major-numbered degree, so map minor degrees through MINOR_DIATONIC.
  const learnMajorDegree =
    perspective === "minor" ? MINOR_DIATONIC[learnDegree - 1].majorDegree : learnDegree;
  const learnChordName =
    perspective === "minor"
      ? minorChordLabel(keyName, learnDegree)
      : chordLabel(keyName, learnDegree);

  // The chosen chord's three inversion voicings, one per lesson card.
  const learnVoicings = useMemo(
    () =>
      INVERSIONS.map(({ id }) =>
        perspective === "minor"
          ? targetMinorInversionMidis(keyName, learnDegree, id, LOW, HIGH)
          : targetInversionMidis(keyName, learnDegree, id, LOW, HIGH)
      ),
    [perspective, keyName, learnDegree]
  );
  const learnTargetMidis = learnVoicings[learnFocus] || null;

  // Learn mode's "try it": any inversion of the chosen chord counts — we
  // just name the one the learner played. No stats, no wrong-answer state.
  const judgeLearnNotes = useCallback(
    (notes) => {
      const found = detectInversion(notes, keyName, learnMajorDegree);
      if (found != null) setLearnFound(found);
    },
    [keyName, learnMajorDegree]
  );

  const clearPicked = useCallback(() => {
    setPickedMidis(EMPTY_SET);
    setPromptStatus((s) => (s === "correct" ? s : null));
    setLearnFound(null);
    setSvStatus((s) => (s === "correct" ? s : null));
    setSvLearnFound(null);
  }, []);

  const playChord = useCallback((midis) => {
    if (!midis) return;
    ensureAudio();
    midis.forEach((m) => playTone(m, { gain: 0.11, durMs: 700 }));
  }, []);

  /* ---------- sevenths mode ---------- */

  // Whether the wheel is currently the sevenths mode's answer sheet.
  const seventhFindKey = mode === "sevenths" && seventhsTab === "drill" && svDrill === "findKey";

  // Same MINOR_DIATONIC remap as the inversion lesson: labels/judging key off
  // the chord's major-numbered degree.
  const svLearnMajorDegree =
    perspective === "minor" ? MINOR_DIATONIC[svLearnDegree - 1].majorDegree : svLearnDegree;
  const svLearnChordName =
    perspective === "minor"
      ? minorSeventhLabel(keyName, svLearnDegree)
      : seventhLabel(keyName, svLearnDegree);

  // The "count down from the root" framing: the selected chord's triad name,
  // its root, the added 7th, and whether that 7th sits a half step (maj7) or
  // a whole step (7 / m7 / ø7) below the root.
  const svLearnTriadName =
    perspective === "minor"
      ? minorChordLabel(keyName, svLearnDegree)
      : chordLabel(keyName, svLearnDegree);
  const svLearnRootName = seventhBassName(keyName, svLearnMajorDegree, 0);
  const svLearnSeventhName = seventhBassName(keyName, svLearnMajorDegree, 3);
  const svLearnIsHalfStep = seventhQuality(svLearnMajorDegree) === "maj7";

  // The chosen seventh chord's four inversion voicings, one per lesson card.
  const svLearnVoicings = useMemo(
    () =>
      SEVENTH_INVERSIONS.map(({ id }) =>
        perspective === "minor"
          ? targetMinorSeventhInversionMidis(keyName, svLearnDegree, id, LOW, HIGH)
          : targetSeventhInversionMidis(keyName, svLearnDegree, id, LOW, HIGH)
      ),
    [perspective, keyName, svLearnDegree]
  );
  const svLearnTarget = svLearnVoicings[svFocus] || null;

  // The midi in a voicing whose pitch class is the chord's 7th (the 4th tone
  // of the root-position stack) — MiniChordDiagram paints it amber.
  const accentSeventh = useCallback(
    (midis, majorDegree) => {
      if (!midis) return null;
      const pcs = seventhPcs(keyName, majorDegree);
      if (!pcs) return null;
      const hit = midis.find((m) => ((m % 12) + 12) % 12 === pcs[3]);
      return hit == null ? null : hit;
    },
    [keyName]
  );

  // Play-it drill target voicing (shown on reveal/success + on the sheet).
  const svTargetMidis = useMemo(
    () =>
      svPrompt
        ? svPrompt.perspective === "minor"
          ? targetMinorSeventhInversionMidis(svPrompt.keyName, svPrompt.minorDegree, svPrompt.inversion, LOW, HIGH)
          : targetSeventhInversionMidis(svPrompt.keyName, svPrompt.degree, svPrompt.inversion, LOW, HIGH)
        : null,
    [svPrompt]
  );

  const nextSeventhPrompt = useCallback((forKey, forPerspective) => {
    setSvPrompt(randomSeventhPrompt(forKey, Math.random, forPerspective));
    setSvStatus(null);
    setSvReveal(false);
    setPickedMidis(EMPTY_SET);
    wrongRecordedRef.current = false;
  }, []);

  // Reroll the play-it prompt when the key/perspective shifts under it.
  useEffect(() => {
    if (mode === "sevenths") nextSeventhPrompt(keyName, perspective);
  }, [keyName, mode, perspective, nextSeventhPrompt]);

  // The lesson's ii–V–I, voice-led, for the one-finger-trick panel.
  const learnTrip = useMemo(() => iiVIChords(keyName, LOW, HIGH), [keyName]);

  const playTrip = useCallback(() => {
    learnTrip.forEach((c, i) => later(() => playChord(c.midis), i * 700));
  }, [learnTrip, later, playChord]);

  // The amber arrow, audible: the key's V7 voiced right next to the tonic
  // triad (smoothSeventh's minimal-motion pick), then the tonic — a plain
  // V7 → I cadence. In minor perspective the same two chords are the honest
  // story too: the VII7 resolving to the relative major.
  const playResolve = useCallback(() => {
    const tonic = anchoredTriad(keyName, 1);
    const v7 = smoothSeventh(tonic, keyName, 5, LOW, HIGH);
    playChord(v7);
    later(() => playChord(tonic), 600);
  }, [keyName, playChord, later]);

  // ii–V–I trip drill: the current stop's three chords, voice-led.
  const tripChords = useMemo(() => iiVIChords(tripKey, LOW, HIGH), [tripKey]);
  const tripNeighbors = wheelNeighbors(tripKey);

  // (Re)start the lap from the selected key whenever the trip comes up (or
  // the learner picks a new key on the wheel mid-trip).
  useEffect(() => {
    if (mode === "sevenths" && seventhsTab === "drill" && svDrill === "trip") {
      setTripKey(keyName);
      setTripStep(0);
      setTripCount(0);
      setTripFlash(false);
    }
  }, [mode, seventhsTab, svDrill, keyName]);

  // Judge a set of midis against whichever sevenths surface is up. Learn:
  // name any inversion of the lesson chord. Play it: exact chord + exact
  // bass. Trip: pc-set only — any inversion/voicing of the current chord.
  const judgeSeventhNotes = useCallback(
    (notes) => {
      if (mode !== "sevenths" || notes.length < 4) return;
      if (seventhsTab === "learn") {
        const found = detectSeventhInversion(notes, keyName, svLearnMajorDegree);
        if (found != null) setSvLearnFound(found);
        return;
      }
      if (svDrill === "play") {
        if (!svPrompt || svStatus === "correct") return;
        const found = detectSeventhInversion(notes, svPrompt.keyName, svPrompt.degree);
        if (found === svPrompt.inversion) {
          setSvStatus("correct");
          setPickedMidis(EMPTY_SET);
          bumpStats("sevenths", true);
          later(() => nextSeventhPrompt(svPrompt.keyName, svPrompt.perspective), 1200);
        } else {
          setSvStatus(found == null ? "wrongNotes" : "wrongBass");
          if (!wrongRecordedRef.current) {
            wrongRecordedRef.current = true;
            bumpStats("sevenths", false);
          }
        }
        return;
      }
      if (svDrill === "trip") {
        if (tripFlash) return;
        const chord = tripChords[tripStep];
        if (!chord || !chord.midis) return;
        if (detectSeventhInversion(notes, tripKey, chord.degree) == null) return;
        bumpStats("sevenths", true);
        setPickedMidis(EMPTY_SET);
        if (tripStep < 2) {
          setTripStep(tripStep + 1);
          return;
        }
        setTripFlash(true);
        setTripCount((n) => n + 1);
        later(() => {
          setTripKey((k) => {
            const n = wheelNeighbors(k);
            return n ? n.subdominant : k;
          });
          setTripStep(0);
          setTripFlash(false);
        }, 1100);
      }
    },
    [
      mode,
      seventhsTab,
      svDrill,
      keyName,
      svLearnMajorDegree,
      svPrompt,
      svStatus,
      tripChords,
      tripStep,
      tripFlash,
      tripKey,
      bumpStats,
      later,
      nextSeventhPrompt,
    ]
  );

  /* ---------- note input (MIDI, on-screen keys, qwerty) ---------- */

  const press = useCallback(
    (midi) => {
      ensureAudio();
      playTone(midi, { gain: 0.12 });
      heldRef.current.add(midi);
      // Held notes (MIDI / A–K row) judge whichever surface is up: a sevenths
      // surface, the inversion lesson's checkmark, or the inversion drill.
      if (mode === "sevenths") {
        judgeSeventhNotes([...heldRef.current]);
      } else if (mode === "inversions" && subTab === "learn") {
        judgeLearnNotes([...heldRef.current]);
      } else {
        judgeHeld(heldRef.current);
      }
      setActiveMidis(new Set(heldRef.current));
    },
    [mode, subTab, judgeHeld, judgeLearnNotes, judgeSeventhNotes]
  );

  const release = useCallback((midi) => {
    if (!heldRef.current.has(midi)) return;
    heldRef.current.delete(midi);
    setActiveMidis(new Set(heldRef.current));
  }, []);

  // Tap-to-select: a mouse-friendly alternative to holding a chord down.
  // Each click toggles the note in `pickedMidis`; once a chord's worth is
  // picked (three for triads, four for sevenths), judge them the same way the
  // held-notes path does. Dropping back below the threshold clears any
  // wrong-answer status so the learner can adjust.
  const tapToggle = useCallback(
    (midi) => {
      ensureAudio();
      playTone(midi, { gain: 0.12 });
      const next = new Set(pickedMidis);
      if (next.has(midi)) next.delete(midi);
      else next.add(midi);
      setPickedMidis(next);
      if (next.size >= (mode === "sevenths" ? 4 : 3)) {
        if (mode === "sevenths") judgeSeventhNotes([...next]);
        else if (subTab === "drill") judgeNotes([...next]);
        else judgeLearnNotes([...next]);
      } else {
        if (promptStatus !== "correct") {
          setPromptStatus(null);
          setLearnFound(null);
        }
        if (svStatus !== "correct") {
          setSvStatus(null);
          setSvLearnFound(null);
        }
      }
    },
    [pickedMidis, mode, subTab, judgeNotes, judgeLearnNotes, judgeSeventhNotes, promptStatus, svStatus]
  );

  const { status: midiStatus, inputs: midiInputs } = useMidiInput(press, release, true);
  const midiReady = midiStatus === "ready" && !!midiInputs && midiInputs.length > 0;

  const kbMap = useMemo(() => {
    const anchor = kbAnchor(LOW, HIGH);
    const map = {};
    KB_KEYS.forEach(({ offset, key }) => {
      const midi = anchor + offset;
      if (midi >= LOW && midi <= HIGH) map[key] = midi;
    });
    return map;
  }, []);

  const kbHints = useMemo(() => {
    const hints = {};
    Object.entries(kbMap).forEach(([key, midi]) => {
      hints[midi] = key;
    });
    return hints;
  }, [kbMap]);

  useEffect(() => {
    const down = (e) => {
      if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = document.activeElement && document.activeElement.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      const midi = kbMap[e.key.toLowerCase()];
      if (midi != null) press(midi);
    };
    const up = (e) => {
      const midi = kbMap[e.key.toLowerCase()];
      if (midi != null) release(midi);
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      Object.values(kbMap).forEach(release);
    };
  }, [kbMap, press, release]);

  /* ---------- explore mode ---------- */

  // Anchored voicings: the tonic sits just below middle C and the degrees
  // climb from it, so stepping up the chords never jumps down an octave.
  // Major perspective anchors on the major tonic; minor anchors on the
  // relative minor's tonic a minor 3rd below it.
  const exploreMidis = useMemo(
    () =>
      perspective === "minor"
        ? anchoredMinorTriad(keyName, exploreChord.degree)
        : anchoredTriad(keyName, exploreChord.degree),
    [keyName, perspective, exploreChord]
  );

  const pickExploreChord = useCallback(
    (degree) => {
      setExploreChord({ degree });
      const midis =
        perspective === "minor" ? anchoredMinorTriad(keyName, degree) : anchoredTriad(keyName, degree);
      playChord(midis);
    },
    [keyName, perspective, playChord]
  );

  const selectKey = useCallback(
    (major, ring) => {
      if (mode === "wheel") {
        if (!wheelPrompt || wheelFlash) return;
        const ok = major === wheelPrompt.answer;
        setWheelFlash({ major, ok });
        bumpStats("wheel", ok);
        later(() => {
          setWheelFlash(null);
          if (ok) setWheelPrompt(randomWheelPrompt());
        }, ok ? 900 : 700);
        return;
      }
      if (seventhFindKey) {
        // The wheel answers "which key is this V7 pulling toward?" — same
        // flash-and-advance rhythm as the wheel quiz, sevenths stats bucket.
        if (!domPrompt || wheelFlash) return;
        const ok = major === domPrompt.answer;
        setWheelFlash({ major, ok });
        bumpStats("sevenths", ok);
        later(() => {
          setWheelFlash(null);
          if (ok) setDomPrompt(randomDominantPrompt());
        }, ok ? 900 : 700);
        return;
      }
      setLastStep(null);
      setKeyName(major);
      setPerspective(ring === "minor" ? "minor" : "major");
      setExploreChord({ degree: 1 });
    },
    [mode, seventhFindKey, domPrompt, wheelPrompt, wheelFlash, bumpStats, later]
  );

  // Move to the brighter (dominant) or darker (subdominant) neighbor, same
  // as clicking that wedge, and remember the step so the scale display can
  // highlight the note that just changed.
  const stepBrightness = useCallback(
    (direction) => {
      const step = brightnessStep(keyName, direction);
      if (!step) return;
      setKeyName(step.key);
      setPerspective("major");
      setExploreChord({ degree: 1 });
      setLastStep({ direction, from: step.from, to: step.to });
    },
    [keyName]
  );

  /* ---------- sheet + roll targets per mode ---------- */

  const sheetDrill = useMemo(() => {
    if (!showSheet) return null;
    if (mode === "explore" && exploreMidis) {
      // VexFlow always reads the MAJOR key signature — a minor label sits on
      // top of the same staff, it doesn't change the key.
      const label =
        perspective === "minor"
          ? minorChordLabel(keyName, exploreChord.degree)
          : chordLabel(keyName, exploreChord.degree);
      return chordSheetDrill(keyName, label, exploreMidis);
    }
    if (mode === "inversions" && subTab === "learn") {
      if (!learnTargetMidis) return null;
      const label = inversionChordLabel(keyName, learnMajorDegree, learnFocus);
      return chordSheetDrill(keyName, label, learnTargetMidis);
    }
    if (mode === "inversions" && subTab === "drill" && prompt && targetMidis) {
      return chordSheetDrill(prompt.keyName, prompt.slashChord, targetMidis);
    }
    if (mode === "sevenths" && seventhsTab === "learn") {
      if (!svLearnTarget) return null;
      const label = seventhSlashLabel(keyName, svLearnMajorDegree, svFocus);
      return chordSheetDrill(keyName, label, svLearnTarget);
    }
    if (mode === "sevenths" && seventhsTab === "drill" && svDrill === "play" && svPrompt && svTargetMidis) {
      return chordSheetDrill(svPrompt.keyName, svPrompt.slashChord, svTargetMidis);
    }
    if (mode === "sevenths" && seventhsTab === "drill" && svDrill === "trip") {
      const chord = tripChords[tripStep];
      return chord && chord.midis ? chordSheetDrill(tripKey, chord.label, chord.midis) : null;
    }
    return null;
  }, [
    showSheet,
    mode,
    subTab,
    keyName,
    perspective,
    exploreChord,
    exploreMidis,
    prompt,
    targetMidis,
    learnTargetMidis,
    learnFocus,
    learnMajorDegree,
    seventhsTab,
    svDrill,
    svPrompt,
    svTargetMidis,
    svLearnTarget,
    svFocus,
    svLearnMajorDegree,
    tripChords,
    tripStep,
    tripKey,
  ]);

  const rollTargets = useMemo(() => {
    if (mode === "explore") return exploreMidis ? new Set(exploreMidis) : EMPTY_SET;
    if (mode === "inversions") {
      if (subTab === "learn") return learnTargetMidis ? new Set(learnTargetMidis) : EMPTY_SET;
      if (targetMidis && (reveal || promptStatus === "correct")) return new Set(targetMidis);
    }
    if (mode === "sevenths") {
      if (seventhsTab === "learn") return svLearnTarget ? new Set(svLearnTarget) : EMPTY_SET;
      if (svDrill === "play" && svTargetMidis && (svReveal || svStatus === "correct")) {
        return new Set(svTargetMidis);
      }
      if (svDrill === "trip") {
        // The trip is a play-along, not a quiz — show the voice-led voicing
        // (any inversion still counts).
        const chord = tripChords[tripStep];
        if (chord && chord.midis) return new Set(chord.midis);
      }
    }
    return EMPTY_SET;
  }, [
    mode,
    subTab,
    exploreMidis,
    targetMidis,
    learnTargetMidis,
    reveal,
    promptStatus,
    seventhsTab,
    svDrill,
    svLearnTarget,
    svTargetMidis,
    svReveal,
    svStatus,
    tripChords,
    tripStep,
  ]);

  // On-screen roll highlight: in inversions/sevenths modes, held notes
  // (MIDI/qwerty) and tap-picked notes both count as "active"; elsewhere just
  // held.
  const rollActiveMidis = useMemo(() => {
    if ((mode !== "inversions" && mode !== "sevenths") || pickedMidis.size === 0) return activeMidis;
    const union = new Set(activeMidis);
    pickedMidis.forEach((m) => union.add(m));
    return union;
  }, [mode, activeMidis, pickedMidis]);

  // Roman-numeral badges marking the selected key's diatonic chords on their
  // wheel wedges (hidden whenever the wheel is an answer sheet: the wheel
  // quiz and the sevenths find-the-key drill).
  const degreeBadges = useMemo(
    () => (mode === "wheel" || seventhFindKey ? null : wheelDegreeBadges(keyName, perspective)),
    [mode, seventhFindKey, keyName, perspective]
  );

  const wrongHint = promptStatus && promptStatus !== "correct" ? WRONG_HINTS[promptStatus] : null;
  const svWrongHint = svStatus && svStatus !== "correct" ? WRONG_HINTS[svStatus] : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-indigo-50 to-violet-50 text-slate-800">
      <header className="mx-auto flex max-w-5xl flex-wrap items-end justify-between gap-2 px-4 pb-2 pt-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">
            Circle of Fifths <span aria-hidden>🎡</span>
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Spin around the keys — explore signatures and chords, then drill your inversions.
          </p>
        </div>
        <div className="flex gap-4 text-sm">
          <Link
            to="/piano-drills"
            className="text-indigo-600 underline-offset-2 hover:underline"
          >
            rhythm builder → piano drills
          </Link>
          <Link
            to="/sillyprojects"
            className="text-slate-400 underline-offset-2 hover:text-slate-600 hover:underline"
          >
            ← silly projects
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-4 px-4 pb-16 pt-4">
        {/* Mode switcher + stats */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex overflow-hidden rounded-lg border border-slate-200 bg-white">
            {MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setMode(m.id)}
                aria-pressed={mode === m.id}
                className={cn(
                  "px-3 py-1.5 text-sm transition-colors",
                  mode === m.id
                    ? "bg-indigo-600 text-white"
                    : "hover:bg-indigo-50 text-slate-600"
                )}
              >
                {m.label}
                {m.wip && (
                  <span
                    className={cn(
                      "ml-1.5 rounded-full px-1.5 py-px align-middle text-[9px] font-semibold uppercase tracking-wide",
                      mode === m.id
                        ? "bg-amber-300 text-amber-900"
                        : "bg-amber-100 text-amber-700"
                    )}
                  >
                    wip
                  </span>
                )}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-1.5 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={showSheet}
              onChange={(e) => setShowSheet(e.target.checked)}
            />
            Sheet music
          </label>
          <span
            className={cn(
              "ml-auto inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium",
              midiReady
                ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                : "border-slate-200 bg-white text-slate-400"
            )}
          >
            <span
              className={cn(
                "h-1.5 w-1.5 rounded-full",
                midiReady ? "bg-emerald-500" : "bg-slate-300"
              )}
            />
            {midiReady ? `MIDI: ${midiInputs[0]}` : "MIDI: none — keys or A–K row work too"}
          </span>
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,4fr)]">
          {/* The wheel */}
          <div className="rounded-xl border border-slate-200 bg-white/80 p-4">
            <Wheel
              selected={mode === "wheel" || seventhFindKey ? null : keyName}
              onSelect={selectKey}
              perspective={mode === "wheel" || seventhFindKey ? "major" : perspective}
              flash={wheelFlash}
              showDetails={mode !== "wheel" && !seventhFindKey}
              degreeBadges={degreeBadges}
              arrow={
                mode === "sevenths" && neighbors
                  ? { from: neighbors.dominant, to: keyName }
                  : null
              }
            />
          </div>

          {/* Mode panel */}
          <div className="space-y-3">
            {mode === "explore" && entry && perspective === "major" && (
              <div className="rounded-xl border border-slate-200 bg-white/80 p-4">
                <h2 className="text-xl font-semibold text-slate-900">
                  {entry.major} major
                  {entry.enharmonicMajor && (
                    <span className="text-slate-400"> (= {entry.enharmonicMajor})</span>
                  )}
                </h2>
                <dl className="mt-2 space-y-1.5 text-sm text-slate-600">
                  <div>
                    <dt className="inline font-medium text-slate-700">Key signature: </dt>
                    <dd className="inline">
                      {signatureLabel(entry)}
                      {entry.accidentals.length > 0 && ` — ${entry.accidentals.join(" ")}`}
                      {entry.enharmonicMajor &&
                        ` · or as ${entry.enharmonicMajor}: 6 flats — ${FLAT_ORDER.slice(0, 6).join(" ")}`}
                    </dd>
                  </div>
                  <div>
                    <dt className="inline font-medium text-slate-700">Relative minor: </dt>
                    <dd className="inline">{entry.minor} (same signature, darker home)</dd>
                  </div>
                  <div>
                    <dt className="inline font-medium text-slate-700">Scale: </dt>
                    <dd className="inline">
                      {scaleNotes(keyName, "major").map((note, i) => (
                        <span key={`${note}-${i}`}>
                          {i > 0 && " "}
                          {lastStep && note === lastStep.to ? (
                            <span
                              className={cn(
                                "rounded px-1 font-semibold",
                                lastStep.direction === "brighter"
                                  ? "bg-indigo-100 text-indigo-700"
                                  : "bg-amber-100 text-amber-700"
                              )}
                            >
                              {note}
                            </span>
                          ) : (
                            note
                          )}
                        </span>
                      ))}
                    </dd>
                  </div>
                  {neighbors && (
                    <div>
                      <dt className="inline font-medium text-slate-700">Neighbors: </dt>
                      <dd className="inline">
                        <button
                          type="button"
                          className="text-indigo-600 hover:underline"
                          onClick={() => selectKey(neighbors.subdominant)}
                        >
                          {neighbors.subdominant}
                        </button>
                        {" ← down a fifth · up a fifth → "}
                        <button
                          type="button"
                          className="text-indigo-600 hover:underline"
                          onClick={() => selectKey(neighbors.dominant)}
                        >
                          {neighbors.dominant}
                        </button>
                      </dd>
                    </div>
                  )}
                </dl>
                <p className="mb-1 mt-3 text-xs font-medium uppercase tracking-wide text-slate-400">
                  Diatonic chords — tap to hear
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {DIATONIC_CHORDS.map((c) => (
                    <button
                      key={c.degree}
                      type="button"
                      onClick={() => pickExploreChord(c.degree)}
                      aria-pressed={exploreChord.degree === c.degree}
                      className={cn(
                        "flex flex-col items-center rounded-lg border px-3 py-1.5 text-sm transition-colors",
                        exploreChord.degree === c.degree
                          ? "border-indigo-400 bg-indigo-50 font-medium"
                          : "border-slate-200 bg-white hover:border-indigo-300"
                      )}
                    >
                      <span className="font-medium">{c.roman}</span>
                      <span className="text-xs text-slate-500">
                        {chordLabel(keyName, c.degree)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {mode === "explore" && perspective === "major" && darkerStep && brighterStep && (
              <div className="rounded-xl border border-slate-200 bg-white/80 p-4">
                <h3 className="text-sm font-semibold text-slate-900">Brighter · darker</h3>
                <div className="mt-2 flex flex-col gap-1.5 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => stepBrightness("darker")}
                    className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-left text-sm transition-colors hover:border-amber-300 hover:bg-amber-50"
                  >
                    🌙 darker → {darkerStep.key} · lower {darkerStep.from} to {darkerStep.to}
                  </button>
                  <button
                    type="button"
                    onClick={() => stepBrightness("brighter")}
                    className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-left text-sm transition-colors hover:border-indigo-300 hover:bg-indigo-50"
                  >
                    ☀️ brighter → {brighterStep.key} · raise {brighterStep.from} to {brighterStep.to}
                  </button>
                </div>
                <p className="mt-2 text-xs text-slate-400">
                  Each step keeps six notes and changes one — sharps brighten, flats darken.
                </p>
              </div>
            )}

            {mode === "explore" && perspective === "major" && modeRows && (
              <div className="rounded-xl border border-slate-200 bg-white/80 p-4">
                <h3 className="text-sm font-semibold text-slate-900">
                  Same home note, darker scales
                </h3>
                <ul className="mt-2 flex flex-col gap-1">
                  {modeRows.map((row) => (
                    <li
                      key={row.mode}
                      className={cn(
                        "flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 rounded-lg px-2 py-1 text-xs sm:text-sm",
                        row.mode === "Ionian" && "bg-indigo-50"
                      )}
                    >
                      <span className="font-medium text-slate-700">
                        {row.tonic} {row.mode}
                        {row.mode === "Ionian" && (
                          <span className="ml-1 font-normal text-indigo-500">
                            — you are here
                          </span>
                        )}
                      </span>
                      <span className="text-slate-400">
                        {row.changed && (
                          <>
                            lower {row.changed.from} → {row.changed.to} ·{" "}
                          </>
                        )}
                        = the {row.sourceKey} major notes
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-slate-400">
                  Each scale borrows a neighbor on the circle — one wedge darker, one note lower.
                </p>
              </div>
            )}

            {mode === "explore" && entry && perspective === "minor" && (
              <div className="rounded-xl border border-slate-200 bg-white/80 p-4">
                <h2 className="text-xl font-semibold text-slate-900">
                  {entry.minor.slice(0, -1)} minor
                </h2>
                <dl className="mt-2 space-y-1.5 text-sm text-slate-600">
                  <div>
                    <dt className="inline font-medium text-slate-700">Key signature: </dt>
                    <dd className="inline">
                      {signatureLabel(entry)} — shared with{" "}
                      <button
                        type="button"
                        className="text-indigo-600 hover:underline"
                        onClick={() => setPerspective("major")}
                      >
                        {entry.major}
                      </button>{" "}
                      major
                    </dd>
                  </div>
                  <div>
                    <dt className="inline font-medium text-slate-700">Scale: </dt>
                    <dd className="inline">{scaleNotes(keyName, "minor").join(" ")}</dd>
                  </div>
                </dl>
                <p className="mb-1 mt-3 text-xs font-medium uppercase tracking-wide text-slate-400">
                  Diatonic chords — tap to hear
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {MINOR_DIATONIC.map((c) => (
                    <button
                      key={c.degree}
                      type="button"
                      onClick={() => pickExploreChord(c.degree)}
                      aria-pressed={exploreChord.degree === c.degree}
                      className={cn(
                        "flex flex-col items-center rounded-lg border px-3 py-1.5 text-sm transition-colors",
                        exploreChord.degree === c.degree
                          ? "border-indigo-400 bg-indigo-50 font-medium"
                          : "border-slate-200 bg-white hover:border-indigo-300"
                      )}
                    >
                      <span className="font-medium">{c.roman}</span>
                      <span className="text-xs text-slate-500">
                        {minorChordLabel(keyName, c.degree)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {mode === "inversions" && (
              <div className="rounded-xl border border-slate-200 bg-white/80 p-4">
                <div className="mb-3 flex w-fit overflow-hidden rounded-md border border-slate-200 bg-white">
                  {INVERSION_SUB_TABS.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setSubTab(t.id)}
                      aria-pressed={subTab === t.id}
                      className={cn(
                        "px-2.5 py-1 text-xs transition-colors",
                        subTab === t.id
                          ? "bg-indigo-600 text-white"
                          : "hover:bg-indigo-50 text-slate-600"
                      )}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {subTab === "learn" && (
                  <>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                      Pick a chord in the key…
                    </p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {(perspective === "minor" ? MINOR_DIATONIC : DIATONIC_CHORDS).map(
                        (c) => (
                          <button
                            key={c.degree}
                            type="button"
                            onClick={() => setLearnDegree(c.degree)}
                            aria-pressed={learnDegree === c.degree}
                            className={cn(
                              "flex flex-col items-center rounded-lg border px-2.5 py-1 text-sm transition-colors",
                              learnDegree === c.degree
                                ? "border-indigo-400 bg-indigo-50 font-medium"
                                : "border-slate-200 bg-white hover:border-indigo-300"
                            )}
                          >
                            <span className="font-medium">{c.roman}</span>
                            <span className="text-xs text-slate-500">
                              {perspective === "minor"
                                ? minorChordLabel(keyName, c.degree)
                                : chordLabel(keyName, c.degree)}
                            </span>
                          </button>
                        )
                      )}
                    </div>
                    <p className="mt-3 text-sm text-slate-700">
                      …{learnChordName} is always the same three notes — root, third,
                      fifth. An inversion just changes which one sits on the bottom:
                    </p>
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      {INVERSIONS.map(({ id, label }) => (
                        <button
                          key={id}
                          type="button"
                          aria-pressed={learnFocus === id}
                          onClick={() => {
                            setLearnFocus(id);
                            playChord(learnVoicings[id]);
                          }}
                          className={cn(
                            "flex flex-col items-center gap-1.5 rounded-lg border p-2 transition-colors",
                            learnFocus === id
                              ? "border-indigo-400 bg-indigo-50"
                              : "border-slate-200 bg-white hover:border-indigo-300"
                          )}
                        >
                          <span className="text-xs font-medium text-slate-700">{label}</span>
                          <MiniChordDiagram midis={learnVoicings[id]} />
                          <span className="text-xs text-slate-500">
                            {inversionChordLabel(keyName, learnMajorDegree, id)} <span aria-hidden>🔊</span>
                          </span>
                        </button>
                      ))}
                    </div>
                    <p className="mt-3 text-sm text-slate-700">
                      The <span className="font-medium text-emerald-600">green key</span> is
                      the bass — the only note that decides the inversion; the others can
                      live in any octave. Play any {learnChordName} on the keys below and
                      I'll name it.
                    </p>
                    {learnFound != null && (
                      <p className="mt-2 text-sm font-medium text-emerald-600">
                        ✓ That's {inversionChordLabel(keyName, learnMajorDegree, learnFound)} —{" "}
                        {INVERSIONS[learnFound].label}!
                      </p>
                    )}
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setSubTab("drill")}
                        className="rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100"
                      >
                        Go drill →
                      </button>
                      {pickedMidis.size > 0 && (
                        <button
                          type="button"
                          onClick={clearPicked}
                          className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm hover:border-indigo-300"
                        >
                          Clear notes
                        </button>
                      )}
                    </div>
                  </>
                )}

                {subTab === "drill" && prompt && (
                  <>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                      Play this — any octave, bass note decides the inversion
                    </p>
                    <p className="mt-1 text-2xl font-semibold text-slate-900">
                      {prompt.chord} — {prompt.inversionLabel}
                    </p>
                    {promptStatus === "correct" ? (
                      <p className="mt-2 text-sm font-medium text-emerald-600">
                        ✓ {prompt.slashChord} — nice. Next one coming…
                      </p>
                    ) : wrongHint ? (
                      <p className="mt-2 text-sm font-medium text-rose-600">{wrongHint}</p>
                    ) : (
                      <p className="mt-2 text-sm text-slate-500">
                        Click three keys to build it — or hold them together on a MIDI keyboard.
                      </p>
                    )}
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => playChord(targetMidis)}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm hover:border-indigo-300"
                      >
                        Hear it
                      </button>
                      <button
                        type="button"
                        onClick={() => setReveal(true)}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm hover:border-indigo-300"
                      >
                        Show me
                      </button>
                      <button
                        type="button"
                        onClick={() => nextInversionPrompt(keyName, perspective)}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm hover:border-indigo-300"
                      >
                        Skip
                      </button>
                      {pickedMidis.size > 0 && (
                        <button
                          type="button"
                          onClick={clearPicked}
                          className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm hover:border-indigo-300"
                        >
                          Clear notes
                        </button>
                      )}
                    </div>
                    <p className="mt-3 text-xs text-slate-400">
                      {perspective === "minor"
                        ? `Drilling ${entry.minor}'s chords — click the outer ring for the major.`
                        : `Drilling in ${keyName} major — pick another key on the wheel.`}{" "}
                      Inversions: {INVERSIONS.map((inv) => inv.label).join(" · ")}.
                    </p>
                  </>
                )}
              </div>
            )}

            {mode === "sevenths" && (
              <div className="rounded-xl border border-slate-200 bg-white/80 p-4">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <div className="flex w-fit overflow-hidden rounded-md border border-slate-200 bg-white">
                  {INVERSION_SUB_TABS.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setSeventhsTab(t.id)}
                      aria-pressed={seventhsTab === t.id}
                      className={cn(
                        "px-2.5 py-1 text-xs transition-colors",
                        seventhsTab === t.id
                          ? "bg-indigo-600 text-white"
                          : "hover:bg-indigo-50 text-slate-600"
                      )}
                    >
                      {t.label}
                    </button>
                  ))}
                  </div>
                  <span className="text-[11px] text-amber-600">
                    🚧 work in progress — more sevenths stuff coming
                  </span>
                </div>

                {seventhsTab === "learn" && (
                  <>
                    <p className="text-sm text-slate-700">
                      Build any seventh chord by adding the note just below your triad's root:
                      a <span className="font-medium text-indigo-600">half step</span> below makes
                      a maj7, a <span className="font-medium text-amber-600">whole step</span>{" "}
                      below makes a 7. Whether that's m7 or plain 7 is just the triad underneath
                      (minor vs major) — and ø7 is the whole-step 7th on the diminished triad.
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-700">
                      <span>
                        {perspective === "minor" ? (
                          <>
                            Every key has exactly{" "}
                            <span className="font-medium text-amber-600">one dominant seventh</span>{" "}
                            — in {entry.minor.slice(0, -1)} minor it's {minorSeventhLabel(keyName, 7)},
                            the VII7 — and it pulls toward the relative major:
                          </>
                        ) : (
                          <>
                            And every major key has exactly{" "}
                            <span className="font-medium text-amber-600">one dominant seventh</span>{" "}
                            — the V7 — and it points home:
                          </>
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={playResolve}
                        className="rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1 text-sm text-amber-800 hover:bg-amber-100"
                      >
                        hear {seventhLabel(keyName, 5)} pull home <span aria-hidden>🔊</span>
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-slate-400">
                      That pull is the amber arrow on the wheel: {seventhLabel(keyName, 5)} →{" "}
                      {keyName}
                      {perspective === "minor" ? ", the relative major" : ""}.
                    </p>
                    <p className="mb-1 mt-3 text-xs font-medium uppercase tracking-wide text-slate-400">
                      Pick a chord in the key…
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {(perspective === "minor" ? MINOR_DIATONIC : DIATONIC_CHORDS).map((c) => {
                        const quality = seventhQuality(
                          perspective === "minor" ? c.majorDegree : c.degree
                        );
                        return (
                          <button
                            key={c.degree}
                            type="button"
                            onClick={() => setSvLearnDegree(c.degree)}
                            aria-pressed={svLearnDegree === c.degree}
                            className={cn(
                              "flex flex-col items-center rounded-lg border px-2.5 py-1 text-sm transition-colors",
                              QUALITY_CHIP[quality],
                              svLearnDegree === c.degree
                                ? "font-medium ring-2 ring-indigo-400"
                                : "hover:brightness-95"
                            )}
                          >
                            <span className="font-medium">{seventhRoman(c.roman, quality)}</span>
                            <span className="text-xs opacity-75">
                              {perspective === "minor"
                                ? minorSeventhLabel(keyName, c.degree)
                                : seventhLabel(keyName, c.degree)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    <p className="mt-1.5 text-xs text-slate-400">
                      The colors — the quality pattern — are the same in every key: one amber
                      dominant seventh, always.
                    </p>
                    <p className="mt-3 text-sm text-slate-700">
                      …{svLearnChordName} = {svLearnTriadName} triad +{" "}
                      <span className="font-medium text-amber-600">{svLearnSeventhName}</span>,{" "}
                      {svLearnIsHalfStep ? "just a half step" : "a whole step"} below{" "}
                      {svLearnRootName}. Four notes, so four inversions:
                    </p>
                    <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                      {SEVENTH_INVERSIONS.map(({ id, label }) => (
                        <button
                          key={id}
                          type="button"
                          aria-pressed={svFocus === id}
                          onClick={() => {
                            setSvFocus(id);
                            playChord(svLearnVoicings[id]);
                          }}
                          className={cn(
                            "flex flex-col items-center gap-1.5 rounded-lg border p-2 transition-colors",
                            svFocus === id
                              ? "border-indigo-400 bg-indigo-50"
                              : "border-slate-200 bg-white hover:border-indigo-300"
                          )}
                        >
                          <span className="text-xs font-medium text-slate-700">{label}</span>
                          <MiniChordDiagram
                            midis={svLearnVoicings[id]}
                            accent={accentSeventh(svLearnVoicings[id], svLearnMajorDegree)}
                          />
                          <span className="text-xs text-slate-500">
                            {seventhSlashLabel(keyName, svLearnMajorDegree, id)}{" "}
                            <span aria-hidden>🔊</span>
                          </span>
                        </button>
                      ))}
                    </div>
                    <p className="mt-3 text-sm text-slate-700">
                      <span className="font-medium text-emerald-600">Green</span> is the bass —
                      still the only note that decides the inversion —{" "}
                      <span className="font-medium text-amber-600">amber</span> is the added 7th.
                      Play any {svLearnChordName} on the keys below and I'll name it.
                    </p>
                    {svLearnFound != null && (
                      <p className="mt-2 text-sm font-medium text-emerald-600">
                        ✓ That's {seventhSlashLabel(keyName, svLearnMajorDegree, svLearnFound)} —{" "}
                        {SEVENTH_INVERSIONS[svLearnFound].label}!
                      </p>
                    )}
                    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
                      <p className="text-sm font-medium text-slate-800">
                        The one-finger trick <span aria-hidden>☝️</span>
                      </p>
                      <p className="mt-1 text-sm text-slate-700">
                        {learnTrip.map((c) => c.label).join(" → ")}: each chord's{" "}
                        <span className="font-medium text-amber-600">7th</span> falls a half-step
                        to become the next chord's 3rd — only one or two fingers move.
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {learnTrip.map((c, i) => (
                          <div key={c.degree} className="flex items-center gap-2">
                            {i > 0 && (
                              <span aria-hidden className="text-slate-400">
                                →
                              </span>
                            )}
                            <div className="flex flex-col items-center gap-1">
                              <MiniChordDiagram
                                midis={c.midis}
                                accent={accentSeventh(c.midis, c.degree)}
                              />
                              <span className="text-xs text-slate-600">{c.label}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={playTrip}
                        className="mt-2 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm hover:border-amber-400"
                      >
                        Hear ii–V–I <span aria-hidden>🔊</span>
                      </button>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setSeventhsTab("drill")}
                        className="rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100"
                      >
                        Go drill →
                      </button>
                      {pickedMidis.size > 0 && (
                        <button
                          type="button"
                          onClick={clearPicked}
                          className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm hover:border-indigo-300"
                        >
                          Clear notes
                        </button>
                      )}
                    </div>
                  </>
                )}

                {seventhsTab === "drill" && (
                  <>
                    <div className="flex w-fit flex-wrap overflow-hidden rounded-md border border-slate-200 bg-white">
                      {SEVENTH_DRILLS.map((d) => (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => setSvDrill(d.id)}
                          aria-pressed={svDrill === d.id}
                          className={cn(
                            "px-2.5 py-1 text-xs transition-colors",
                            svDrill === d.id
                              ? "bg-indigo-600 text-white"
                              : "hover:bg-indigo-50 text-slate-600"
                          )}
                        >
                          {d.label}
                        </button>
                      ))}
                    </div>

                    {svDrill === "play" && svPrompt && (
                      <>
                        <p className="mt-3 text-xs font-medium uppercase tracking-wide text-slate-400">
                          Play this — any octave, bass note decides the inversion
                        </p>
                        <p className="mt-1 text-2xl font-semibold text-slate-900">
                          {svPrompt.chord} — {svPrompt.inversionLabel}{" "}
                          <span className="text-base font-normal text-slate-500">
                            ({seventhBassName(svPrompt.keyName, svPrompt.degree, svPrompt.inversion)}{" "}
                            in the bass)
                          </span>
                        </p>
                        {svStatus === "correct" ? (
                          <p className="mt-2 text-sm font-medium text-emerald-600">
                            ✓ {svPrompt.slashChord} — nice. Next one coming…
                          </p>
                        ) : svWrongHint ? (
                          <p className="mt-2 text-sm font-medium text-rose-600">{svWrongHint}</p>
                        ) : (
                          <p className="mt-2 text-sm text-slate-500">
                            Click four keys to build it — or hold them together on a MIDI keyboard.
                          </p>
                        )}
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => playChord(svTargetMidis)}
                            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm hover:border-indigo-300"
                          >
                            Hear it
                          </button>
                          <button
                            type="button"
                            onClick={() => setSvReveal(true)}
                            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm hover:border-indigo-300"
                          >
                            Show me
                          </button>
                          <button
                            type="button"
                            onClick={() => nextSeventhPrompt(keyName, perspective)}
                            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm hover:border-indigo-300"
                          >
                            Skip
                          </button>
                          {pickedMidis.size > 0 && (
                            <button
                              type="button"
                              onClick={clearPicked}
                              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm hover:border-indigo-300"
                            >
                              Clear notes
                            </button>
                          )}
                        </div>
                        <p className="mt-3 text-xs text-slate-400">
                          {perspective === "minor"
                            ? `Drilling ${entry.minor}'s sevenths — click the outer ring for the major.`
                            : `Drilling in ${keyName} major — pick another key on the wheel.`}{" "}
                          Inversions: {SEVENTH_INVERSIONS.map((inv) => inv.label).join(" · ")}.
                        </p>
                      </>
                    )}

                    {svDrill === "findKey" && domPrompt && (
                      <>
                        <p className="mt-3 text-xs font-medium uppercase tracking-wide text-slate-400">
                          Answer by clicking the wheel
                        </p>
                        <p className="mt-1 text-2xl font-semibold text-slate-900">{domPrompt.chord}</p>
                        <p className="mt-1 text-sm text-slate-600">
                          One dominant seventh — which key is it pulling toward?
                        </p>
                        {wheelFlash && !wheelFlash.ok && (
                          <p className="mt-2 text-sm font-medium text-rose-600">
                            Not {wheelFlash.major} — try again.
                          </p>
                        )}
                        {wheelFlash && wheelFlash.ok && (
                          <p className="mt-2 text-sm font-medium text-emerald-600">✓ Got it.</p>
                        )}
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => playChord(domPrompt.midis)}
                            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm hover:border-indigo-300"
                          >
                            Hear it
                          </button>
                        </div>
                      </>
                    )}

                    {svDrill === "trip" && (
                      <>
                        <p className="mt-3 text-xs font-medium uppercase tracking-wide text-slate-400">
                          The jazz lap — falling fifths around the wheel
                        </p>
                        <p className="mt-1 text-sm text-slate-600">
                          Play each chord in turn — any inversion or voicing counts.
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {tripChords.map((c, i) => (
                            <div key={c.degree} className="flex items-center gap-2">
                              {i > 0 && (
                                <span aria-hidden className="text-slate-400">
                                  →
                                </span>
                              )}
                              <span
                                aria-current={i === tripStep && !tripFlash ? "step" : undefined}
                                className={cn(
                                  "rounded-lg border px-3 py-1.5 text-sm",
                                  tripFlash || i < tripStep
                                    ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                                    : i === tripStep
                                    ? "border-amber-400 bg-amber-100 font-medium text-amber-800"
                                    : "border-slate-200 bg-white text-slate-500"
                                )}
                              >
                                {c.label}
                              </span>
                            </div>
                          ))}
                        </div>
                        {tripFlash ? (
                          <p className="mt-2 text-sm font-medium text-emerald-600">
                            ✓ {tripKey} done — sliding down a fifth to{" "}
                            {tripNeighbors ? tripNeighbors.subdominant : "…"}…
                          </p>
                        ) : (
                          <p className="mt-2 text-sm text-slate-500">
                            In {tripKey}: land all three and we slide down a fifth.
                          </p>
                        )}
                        <p className="mt-2 text-xs text-slate-400">
                          Lap {Math.floor(tripCount / 12) + 1} · {tripCount % 12}/12 keys
                        </p>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {mode === "wheel" && wheelPrompt && (
              <div className="rounded-xl border border-slate-200 bg-white/80 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  Answer by clicking the wheel
                </p>
                <p className="mt-1 text-xl font-semibold text-slate-900">
                  {wheelPrompt.question}
                </p>
                {wheelFlash && !wheelFlash.ok && (
                  <p className="mt-2 text-sm font-medium text-rose-600">
                    Not {wheelFlash.major} — try again.
                  </p>
                )}
                {wheelFlash && wheelFlash.ok && (
                  <p className="mt-2 text-sm font-medium text-emerald-600">✓ Got it.</p>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <StatChip label="Inversions" stat={stats.inversions} />
              <StatChip label="Sevenths" stat={stats.sevenths} />
              <StatChip label="Find the key" stat={stats.wheel} />
            </div>
          </div>
        </div>

        {mode !== "wheel" && (
          <>
            {sheetDrill && (
              <SheetView drill={sheetDrill} currentEventIndices={[]} statuses={{}} />
            )}
            <PianoRoll
              low={LOW}
              high={HIGH}
              activeMidis={rollActiveMidis}
              targetMidis={rollTargets}
              onPress={mode === "inversions" || mode === "sevenths" ? tapToggle : press}
              onRelease={mode === "inversions" || mode === "sevenths" ? NOOP : release}
              kbHints={kbHints}
              useFlats={Boolean(entry && entry.useFlats)}
            />
            <p className="text-center text-xs text-slate-400">
              Plug in a MIDI keyboard (picked up automatically), click the keys, or use the
              A–K row.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
